import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { IsActiveMatchOptions, Router, RouterOutlet } from '@angular/router';
import { BaseLayoutComponent, BREAKPOINTS } from '@edt/layout';
import { HeaderComponent } from '../components/header/header.component';
import { BreakpointObserver } from '@angular/cdk/layout';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { interval, map, tap } from 'rxjs';
import { LayoutService } from '../service/layout.service';
import { ShoppingCartMjsComponent } from '../../cart/components/shopping-cart-mjs/shopping-cart-mjs.component';
import { BreadcrumbsService } from '@edt/layout/lib/breadcrumbs/breadcrumbs.service';
import { FooterComponent } from '@edt/components/molecules/footer/footer.component';
import { GlobalStore } from '@/store/app.store';
import { OptionLink } from '../models/link';
import { BreadcrumbsComponent } from '@edt/layout/lib/breadcrumbs/breadcrumbs.component';
import { HorizontalMenuComponent } from '../components/horizontal-menu-2/horizontal-menu.component';
import { StoreProductCategory } from '@medusajs/types';
import { SideMenuItemComponent } from '../components/side-menu-item/side-menu-item.component';
import { AnnouncementBarComponent } from '../../../@edt/components/molecules/announcement-bar/announcement-bar.component';
import { NgProgressbar } from 'ngx-progressbar';
import { NgProgressRouter } from 'ngx-progressbar/router';
import { FullscreenLoaderComponent } from '@/cart/components/fullscreen-loader/fullscreen-loader.component';

@Component({
  selector: 'store-layout',
  standalone: true,
  templateUrl: './store-layout.component.html',
  styleUrls: ['./store-layout.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [LayoutService, BreadcrumbsService],
  imports: [
    BaseLayoutComponent,
    MatButtonModule,
    MatIconModule,
    RouterOutlet,
    HeaderComponent,
    ShoppingCartMjsComponent,
    HorizontalMenuComponent,
    FooterComponent,
    BreadcrumbsComponent,
    SideMenuItemComponent,
    AnnouncementBarComponent,
    NgProgressbar,
    NgProgressRouter,
    FullscreenLoaderComponent,
  ],
})
export class StoreLayoutComponent {
  destroyRef = inject(DestroyRef);
  #breakpointObserver: BreakpointObserver = inject(BreakpointObserver);
  #layoutSrv = inject(LayoutService);
  globalStore = inject(GlobalStore);
  siteInfo = this.globalStore.siteInfo;
  footerComponentTheme = this.globalStore.footer;
  #route = inject(Router);
  #isAnnouncementIntervalActive = false;

  isHandset = toSignal(
    this.#breakpointObserver
      .observe(BREAKPOINTS.md)
      .pipe(map(result => result.matches)),
    { initialValue: false }
  );
  canShow = false;
  footerTheme: Record<string, string> = {
    height: '40px',
  };
  readonly myMatchOptions: IsActiveMatchOptions = {
    queryParams: 'ignored',
    matrixParams: 'exact',
    paths: 'exact',
    fragment: 'exact',
  };
  containerTheme: Record<string, string> = {
    'min-height': 'calc(100vh - 84px)',
    // background: '#f3f4f3',
    position: 'relative',
    margin: '0',
    'border-radius': '0',
    'scroll-behavior': 'smooth',
  };
  sideLeftTheme = computed<Record<string, string>>(() => {
    const width = this.isHandset() ? '100vw' : '350px';
    return {
      'min-width': '200px !important',
      width,
    };
  });
  sideRightTheme = computed<Record<string, string>>(() => {
    const width = this.isHandset() ? '100vw' : 'max(30vw, 320px)';
    return {
      'min-width': '320px !important',
      'flex-shrink': '0',
      width,
    };
  });
  showSearch = computed(() => {
    return this.#layoutSrv.showSearch();
  });
  rightOpened = this.#layoutSrv.rightMenuOpened;
  leftOpened = this.#layoutSrv.leftMenuOpened;

  authMenuItems = computed<OptionLink[]>(() => {
    return !this.globalStore.authUserData() && this.isHandset()
      ? [
          {
            label: 'Iniciar Sesión',
            handle: 'iniciar-sesión',
            routerUrl: '/seguridad/iniciar',
          },
          {
            label: 'Registrarse',
            handle: 'registrarse',
            routerUrl: '/seguridad/registro',
          },
        ]
      : [];
  });

  customerMenuItems = computed<OptionLink[]>(() => {
    return this.globalStore.authUserData() && this.isHandset()
      ? [
          {
            label: 'Perfil',
            handle: 'perfil',
            routerUrl: '/usuario/perfil',
          },
          {
            label: 'Mis destinatarios',
            handle: 'mis-destinatarios',
            routerUrl: 'usuario/direcciones',
          },

          {
            label: 'Mis pedidos',
            handle: 'mis-pedidos',
            routerUrl: 'usuario/ordenes',
          },
        ]
      : [];
  });

  categoriesMenuItem = computed(() => {
    return this.globalStore
      .categories()
      .map(cat => ({ ...cat, rank: (cat.rank ?? 0) + 1 }))
      .sort((cat1, cat2) => cat1.rank - cat2.rank)
      .map<OptionLink>(cat => this._parseCategoryToOptionLink(cat));
  });

  private _parseCategoryToOptionLink(cat: StoreProductCategory): OptionLink {
    const option: OptionLink = {
      handle: cat.handle,
      label: cat.name,
      routerUrl: `/productos/${cat.handle}`,
      selected: false,
      isCategory: true,
    };

    if (cat.category_children.length > 0) {
      option.children = cat.category_children.map(category =>
        this._parseCategoryToOptionLink(category)
      );
    }

    return option;
  }

  isHomeEnabled = computed(() => this.globalStore.home()?.enabled === true);

  sideNavMenuLeftMenuItems = computed(() => {
    return [
      ...(this.isHomeEnabled() ? [this.homePageOptionLink] : []),
      ...this.authMenuItems(),
      this.allProductPageOptionLink,
      ...this.categoriesMenuItem().slice(0, 5),
      ...this.customerMenuItems(),
      ...this.staticPageOptionsLinks,
    ];
  });

  horizontalMenuItems = computed(() => {
    return [
      ...(this.isHomeEnabled() ? [this.homePageOptionLink] : []),
      this.allProductPageOptionLink,
      ...this.categoriesMenuItem().slice(0, 5),
      ...this.staticPageOptionsLinks,
    ];
  });

  announcementBarConfig = computed(() =>
    this.globalStore.announcementBarConfig()
  );
  selectedCategoryHandles = computed(() =>
    this.globalStore.selectedCategories().map(cat => cat.handle)
  );

  homePageOptionLink: OptionLink = {
    label: 'Inicio',
    handle: 'inicio',
    routerUrl: '/inicio',
    selected: false,
  };

  allProductPageOptionLink: OptionLink = {
    label: 'Todos los productos',
    handle: 'listar',
    routerUrl: '/productos/listar',
    isCategory: true,
  };

  staticPageOptionsLinks: OptionLink[] = [
    {
      label: 'Ayuda',
      handle: 'ayuda',
      routerUrl: '/ayuda',
    },
    {
      label: 'Sobre nosotros',
      handle: 'sobre-nosotros',
      routerUrl: '/sobre-nosotros',
    },

    {
      label: 'Preguntas frecuentes',
      handle: 'preguntas-frecuentes',
      routerUrl: '/preguntas-frecuentes',
    },
  ];

  constructor() {
    effect(() => {
      const config = this.globalStore.announcementBarConfig();

      if (config && !this.#isAnnouncementIntervalActive) {
        this.#isAnnouncementIntervalActive = true;
        interval(config.announcementsRefreshInterval)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            tap(() =>
              this.globalStore.getAnnouncements({
                url: '/announcement-activated',
                filters: {
                  fromObject: {
                    _t: Date.now().toString(),
                  },
                },
              })
            )
          )
          .subscribe();
      }
    });
  }

  rightMenuChange(ev: boolean): void {
    this.#layoutSrv.toggleRightMenu(ev);
  }
  leftMenuChange(ev: boolean): void {
    this.#layoutSrv.toggleLeftMenu(ev);
  }
  closeLeftMenu(): void {
    this.#layoutSrv.toggleLeftMenu(false);
  }

  goCheckout(): void {
    this.#layoutSrv.toggleRightMenu(false);
    this.#route.navigate(['checkout']);
  }

  goToSiteHome() {
    this.#route.navigate(['/']);
    this.globalStore.selectCategories([]);
  }

  onSideMenuItemClicked(item: OptionLink) {
    if (item && item.isCategory) {
      switch (item.handle) {
        case 'listar':
          this.globalStore.selectCategories([]);
          break;
        default:
          this.globalStore.selectCategories([item.handle]);
          break;
      }
    }
    if (this.isHandset()) {
      this.closeLeftMenu();
    }
  }

  onOptionLinkSelected(item: OptionLink) {
    if (item && item.isCategory) {
      switch (item.handle) {
        case 'listar':
          this.globalStore.selectCategories([]);
          break;
        default:
          this.globalStore.selectCategories([item.handle]);
          break;
      }
      this.#route.navigate(['productos', item.handle]);
    } else if (item && !item.isCategory) {
      this.#route.navigate([item.handle]);
    }
  }

  announcements = computed(() => this.globalStore.announcements() ?? []);
}
